import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Menu, X, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV_LINKS = [
  { label: "Features", to: "/features" },
  { label: "Pricing", to: "/plans" },
  { label: "About", to: "/about" },
  { label: "Press", to: "/press" },
  { label: "Status", to: "/status" },
  { label: "Contact", to: "/contact" },
];

function OmniLogo({ className = "" }) {
  return (
    <Link to="/" className={`flex items-center gap-3 group ${className}`} data-testid="website-logo">
      <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 transition-transform duration-300 group-hover:scale-105">
        <img src="/icon-96x96.png" alt="Omni" className="w-full h-full object-cover" />
      </div>
      <span className="text-xl font-semibold tracking-tight text-white" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
        Omni
      </span>
    </Link>
  );
}

function WebsiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <>
      <nav
        data-testid="website-nav"
        className="fixed top-0 left-0 right-0 z-[100] transition-all duration-500"
        style={{
          background: scrolled ? "rgba(6,9,18,0.85)" : "rgba(6,9,18,0.4)",
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid transparent",
        }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 lg:px-12 h-[72px]">
          <OmniLogo />

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map(l => (
              <Link
                key={l.to}
                to={l.to}
                className={`text-sm transition-colors duration-200 ${location.pathname === l.to ? "text-white" : "text-[#A1A5B3] hover:text-white"}`}
              >
                {l.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/auth" className="text-sm text-[#A1A5B3] hover:text-white transition-colors">Log in</Link>
            <Link to="/auth" data-testid="website-get-started-btn">
              <button className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium text-white transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: "#E31212", boxShadow: "0 0 20px rgba(227,18,18,0.3)" }}>
                Get Started <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="website-mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[99] pt-[72px]"
            style={{ background: "#060912" }}
          >
            <div className="flex flex-col p-6 gap-1">
              {NAV_LINKS.map(l => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="flex items-center justify-between py-4 border-b text-lg text-white"
                  style={{ borderColor: "rgba(255,255,255,0.08)" }}
                >
                  {l.label}
                  <ChevronRight className="w-5 h-5 text-[#6E7385]" />
                </Link>
              ))}
              <div className="mt-8 flex flex-col gap-3">
                <Link to="/auth">
                  <button className="w-full py-3.5 rounded-full text-base font-medium text-white"
                    style={{ background: "#E31212", boxShadow: "0 0 20px rgba(227,18,18,0.3)" }}>
                    Get Started Free
                  </button>
                </Link>
                <Link to="/auth">
                  <button className="w-full py-3.5 rounded-full text-base text-[#A1A5B3] border" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                    Log in
                  </button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function WebsiteFooter() {
  const footerLinks = [
    {
      title: "Product",
      links: [
        { label: "Features", to: "/features" },
        { label: "Pricing", to: "/plans" },
        { label: "Press Kit", to: "/press" },
        { label: "Status", to: "/status" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About", to: "/about" },
        { label: "Contact", to: "/contact" },
        { label: "masterglobalinc.com", to: "https://masterglobalinc.com", external: true },
      ],
    },
    {
      title: "Legal",
      links: [
        { label: "Privacy Policy", to: "/privacy" },
        { label: "Terms of Service", to: "/terms" },
      ],
    },
  ];

  return (
    <footer data-testid="website-footer" className="relative overflow-hidden" style={{ background: "#060912" }}>
      {/* Background watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
        <span className="text-[20vw] font-bold tracking-tighter opacity-[0.02] text-white" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
          OMNI
        </span>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-12">
        {/* Top section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-20">
          {/* Brand */}
          <div className="lg:col-span-2">
            <OmniLogo className="mb-6" />
            <p className="text-[#6E7385] text-sm leading-relaxed max-w-xs mb-6">
              Your AI life companion. Six guides, twenty-two mentors, one hundred and thirteen languages — always on your side.
            </p>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-xs text-[#A1A5B3]">All systems operational</span>
            </div>
          </div>

          {/* Link columns */}
          {footerLinks.map(col => (
            <div key={col.title}>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6E7385] mb-5">{col.title}</p>
              <div className="flex flex-col gap-3">
                {col.links.map(l =>
                  l.external ? (
                    <a key={l.label} href={l.to} target="_blank" rel="noopener noreferrer" className="text-sm text-[#A1A5B3] hover:text-white transition-colors">
                      {l.label}
                    </a>
                  ) : (
                    <Link key={l.label} to={l.to} className="text-sm text-[#A1A5B3] hover:text-white transition-colors">
                      {l.label}
                    </Link>
                  )
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="border-t mb-8" style={{ borderColor: "rgba(255,255,255,0.06)" }} />

        {/* Bottom */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6">
            <p className="text-xs text-[#6E7385]">&copy; {new Date().getFullYear()} Omni. All rights reserved.</p>
            <a href="https://masterglobalinc.com" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs text-[#F5A623] hover:text-[#FFD080] transition-colors" data-testid="master-global-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F5A623]" />
              A Masters Global Inc. Product
            </a>
          </div>
          <p className="text-[11px] text-[#6E7385] text-center md:text-right max-w-md">
            Omni is an AI companion, not a licensed professional. In a crisis, contact emergency services.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default function WebsiteLayout({ children }) {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen" style={{ background: "#060912", color: "#fff" }}>
      <WebsiteNav />
      <main>{children}</main>
      <WebsiteFooter />
    </div>
  );
}
